import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Props for a single pagination button
 */
interface PaginationButtonProps {
  /** Page this button navigates to */
  page: number;
  /** Button label */
  children: ReactNode;
  /** Callback when the button is pressed */
  onPageChange: (page: number) => void;
  /** Whether the button is inactive */
  disabled?: boolean;
  /** Whether the button represents the current page */
  active?: boolean;
}

/**
 * Einzelner Schalter der Seitenblaetterung.
 *
 * Bewusst auf Modulebene definiert: Innerhalb von `Pagination` deklariert
 * waere es bei jedem Render ein neuer Komponententyp, wodurch React den
 * Teilbaum jedes Mal neu einhaengt.
 */
export const PaginationButton = ({
  page,
  children,
  onPageChange,
  disabled,
  active,
}: PaginationButtonProps) => (
  <motion.button
    type="button"
    onClick={() => !disabled && onPageChange(page)}
    disabled={disabled}
    whileHover={disabled ? undefined : { scale: 1.05 }}
    whileTap={disabled ? undefined : { scale: 0.95 }}
    className={cn(
      'min-w-[36px] h-9 px-3',
      'rounded-lg text-sm font-medium',
      'transition-all duration-200',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
      active
        ? 'bg-primary/80 text-white border border-primary/40'
        : 'bg-white/[0.06] text-surface-700 border border-white/[0.08] hover:bg-white/[0.12] hover:border-white/[0.15]',
      disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
    )}
    aria-current={active ? 'page' : undefined}
  >
    {children}
  </motion.button>
);
